/**
 * A band's human ordinal ("5ta") comes from its POSITION in the sport's list,
 * never from its name: the API returns bands ascending by `minimumElo` and the
 * backend validates sums against ids consecutive in that same order, so
 * position is the only axis that agrees with the server.
 */

import type { Category } from '@/types/tournament.model';

/** All these helpers need from a `Category`. */
export type CategoryRef = Pick<Category, 'id' | 'name'>;

function getCategoryOrdinal(index: number, total: number): number | null {
  if (index < 0 || index >= total) return null;
  return total - index;
}

/**
 * Short chip for the slider: "5°". Squash's second band is named "Intermedia"
 * and keeps its own chip so the label matches what organizers call it.
 */
export function getCategoryAbbreviation(
  index: number,
  categories: CategoryRef[],
): string {
  const name = categories[index]?.name ?? '';
  if (name.split(' ')[0].toLowerCase() === 'intermedia') return 'Int.';

  const ordinal = getCategoryOrdinal(index, categories.length);
  if (ordinal != null) return `${ordinal}°`;

  return name.charAt(0).toUpperCase();
}

export function getCategorySum(
  minCategory: CategoryRef | null | undefined,
  maxCategory: CategoryRef | null | undefined,
  categories: CategoryRef[],
): number | null {
  if (!minCategory || !maxCategory) return null;

  const total = categories.length;
  const minOrdinal = getCategoryOrdinal(
    categories.findIndex((c) => c.id === minCategory.id),
    total,
  );
  const maxOrdinal = getCategoryOrdinal(
    categories.findIndex((c) => c.id === maxCategory.id),
    total,
  );
  if (minOrdinal == null || maxOrdinal == null) return null;

  return minOrdinal + maxOrdinal;
}

/**
 * Sums with at least two distinct pairs: 3 … 2n-1. The ends (2 and 2n) only
 * admit a pair of two equal bands, which is a single-category tournament.
 */
export function getSumOptions(categories: CategoryRef[]): number[] {
  const n = categories.length;
  if (n < 2) return [];

  const options: number[] = [];
  for (let sum = 3; sum <= 2 * n - 1; sum++) options.push(sum);
  return options;
}

/**
 * Indices of the widest pair whose ordinals add up to `sum` — the encoding that
 * honestly describes who may register. Null when the sum is unreachable.
 */
export function getIndexPairForSum(
  sum: number,
  total: number,
): [number, number] | null {
  if (total < 2) return null;

  const indexSum = 2 * total - sum;
  if (indexSum < 0 || indexSum > 2 * (total - 1)) return null;

  const minIndex = Math.max(0, indexSum - (total - 1));
  return [minIndex, indexSum - minIndex];
}

/**
 * Display string, e.g. "Suma 9". Null when the restriction does not apply: no
 * `forceSum`, a single category, or the sport's bands not loaded yet.
 */
export function getCategorySumLabel(
  minCategory: CategoryRef | null | undefined,
  maxCategory: CategoryRef | null | undefined,
  forceSum: boolean | undefined,
  categories: CategoryRef[],
): string | null {
  if (!forceSum || !minCategory || !maxCategory) return null;
  if (minCategory.id === maxCategory.id) return null;

  const sum = getCategorySum(minCategory, maxCategory, categories);
  return sum == null ? null : `Suma ${sum}`;
}
